/**
 * Report Analytics - Compliance tracking and report metrics
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { getReportAnalytics } from '../../api/reports';
import {
  TrendingUp,
  Eye,
  Download,
  Share2,
  Clock,
  CheckCircle,
  AlertCircle,
  Users,
  Calendar,
  BarChart3,
} from 'lucide-react';

interface ReportAnalyticsProps {
  reportId: string;
}

export function ReportAnalytics({ reportId }: ReportAnalyticsProps) {
  const { data: analytics, isLoading } = useQuery({
    queryKey: ['report-analytics', reportId],
    queryFn: () => getReportAnalytics(reportId),
    enabled: !!reportId,
  });

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </Card>
    );
  }

  if (!analytics) {
    return (
      <Card>
        <p className="text-center py-12 text-gray-500">No analytics data available</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Usage Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-600 rounded-lg">
              <Eye className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{analytics.views}</div>
              <div className="text-sm text-gray-600">Total Views</div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-600 rounded-lg">
              <Download className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{analytics.downloads}</div>
              <div className="text-sm text-gray-600">Downloads</div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-600 rounded-lg">
              <Share2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{analytics.shares}</div>
              <div className="text-sm text-gray-600">Shares</div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 dark:from-orange-900/20 dark:to-orange-800/20 dark:border-orange-700">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-600 dark:bg-orange-500 rounded-lg">
              <Clock className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {analytics.avg_time_spent_seconds && !isNaN(analytics.avg_time_spent_seconds) && analytics.avg_time_spent_seconds > 0
                  ? `${Math.round(analytics.avg_time_spent_seconds / 60)}m`
                  : analytics.status === 'in_progress' || analytics.status === 'generating'
                  ? 'In Progress'
                  : 'N/A'}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Avg Time</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Viewers List */}
      <Card title="Recent Viewers" className="dark:bg-theme-paper dark:border-theme-border">
        <div className="space-y-3">
          {analytics.viewers && analytics.viewers.length > 0 ? (
            analytics.viewers.map((viewer, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full w-10 h-10 flex items-center justify-center font-medium">
                    {viewer.user_name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{viewer.user_name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {viewer.view_count} view{viewer.view_count !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Last viewed: {viewer.last_viewed_at ? new Date(viewer.last_viewed_at).toLocaleDateString() : 'N/A'}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400 mb-2">No viewer data available</p>
              {analytics.status === 'in_progress' || analytics.status === 'generating' ? (
                <p className="text-sm text-gray-400 dark:text-gray-500 italic">Report is still being generated</p>
              ) : null}
            </div>
          )}
        </div>
      </Card>

      {/* Last Activity */}
      {analytics.last_viewed_at && (
        <Card>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-700">Last Viewed</div>
                <div className="text-gray-900 dark:text-gray-100">
                  {new Date(analytics.last_viewed_at).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

